import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/services", key: "nav.services" },
  { to: "/about", key: "nav.about" },
  { to: "/seo", key: "nav.seo" },
  { to: "/blog", key: "nav.blog" },
  { to: "/faq", key: "nav.faq" },
  { to: "/contact", key: "nav.contact" },
];

export default function Header() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  const isAuthed = user && user !== false;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-amber-500/15" : "bg-transparent"}`} data-testid="site-header">
      <div className="container-lux flex items-center justify-between h-16 sm:h-20">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}-link`}
              className="text-sm font-medium text-slate-300 hover:text-amber-400 transition-colors">
              {t(l.key)}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          {isAuthed ? (
            <div className="flex items-center gap-2">
              <Link to="/dashboard" className="gold-btn !py-2 !px-4 text-sm" data-testid="header-dashboard-button">
                <LayoutDashboard size={16} /> {t("nav.dashboard")}
              </Link>
              <button onClick={() => { logout(); nav("/"); }} className="silver-btn !py-2 !px-3 text-sm" data-testid="header-logout-button">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" className="silver-btn !py-2 !px-4 text-sm" data-testid="header-login-button">{t("nav.login")}</Link>
              <Link to="/register" className="gold-btn !py-2 !px-4 text-sm" data-testid="header-register-button">{t("nav.register")}</Link>
            </>
          )}
        </div>
        <button className="lg:hidden text-slate-200 p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-amber-500/15" data-testid="mobile-menu">
          <div className="container-lux py-5 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="py-2.5 text-slate-200 hover:text-amber-400 font-medium border-b border-white/5" data-testid={`mobile-nav-${l.to.slice(1)}-link`}>
                {t(l.key)}
              </Link>
            ))}
            <div className="flex items-center justify-between pt-4">
              <LanguageSwitcher />
            </div>
            <div className="flex gap-2 pt-3">
              {isAuthed ? (
                <>
                  <Link to="/dashboard" className="gold-btn flex-1 text-sm" data-testid="mobile-dashboard-button">{t("nav.dashboard")}</Link>
                  <button onClick={() => { logout(); nav("/"); }} className="silver-btn text-sm" data-testid="mobile-logout-button"><LogOut size={16} /></button>
                </>
              ) : (
                <>
                  <Link to="/login" className="silver-btn flex-1 text-sm" data-testid="mobile-login-button">{t("nav.login")}</Link>
                  <Link to="/register" className="gold-btn flex-1 text-sm" data-testid="mobile-register-button">{t("nav.register")}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
